import { useEffect, useMemo, useRef } from 'react';
import styles from './AsciiArt.module.scss';

/** How long the whole grid takes to stop churning and settle. */
const SETTLE_MS = 1500;
/** Redraws per second while animating. Static reads fine well under 60. */
const FPS = 24;
/** Cursor influence radius, in character cells. */
const RIPPLE_RADIUS = 5.5;
/** How long a cell disturbed by the cursor takes to fall back into place. */
const RIPPLE_DECAY_MS = 420;

/** The grid resolved to its finished state, rows joined with newlines. */
const resolve = ({ cols, rows, cells }) => {
  let out = '';
  for (let row = 0; row < rows; row += 1) {
    out += `${cells.slice(row * cols, (row + 1) * cols)}\n`;
  }
  return out;
};

/**
 * A character grid that scrambles itself into focus and ripples under the cursor.
 *
 * Renders into a single text node updated on a rAF loop rather than one element
 * per cell — over a thousand React children re-rendering 24 times a second would
 * be needlessly expensive for what is ultimately one string.
 *
 * @param {{cols: number, rows: number, ramp: string, cells: string}} grid
 *   The artwork. `cells` is `cols * rows` characters drawn from `ramp`, read
 *   left to right, top to bottom. Must be a stable reference — pass a module
 *   constant, not an object literal.
 * @param {string} [label]
 *   Accessible name. Without one the grid is marked decorative, which is what
 *   you want unless the characters themselves carry meaning: a screen reader
 *   would otherwise read the raw glyphs aloud.
 */
function AsciiArt({ grid, label, className }) {
  const preRef = useRef(null);
  const frameRef = useRef(null);
  const finalText = useMemo(() => resolve(grid), [grid]);

  // Scale the type so the grid exactly spans its column. The stylesheet gets
  // close using container query units, but the real advance width varies by a
  // few percent across monospace fonts, so measure it rather than assume.
  useEffect(() => {
    const node = preRef.current;
    const frame = frameRef.current;
    if (!node || !frame || typeof ResizeObserver === 'undefined') return undefined;

    const range = document.createRange();
    let scheduled = 0;

    const fit = () => {
      scheduled = 0;
      const available = frame.clientWidth;
      if (!available) return;

      // Drop any previous correction so the CSS value is what gets measured.
      node.style.fontSize = '';
      const base = Number.parseFloat(window.getComputedStyle(node).fontSize);
      range.selectNodeContents(node);
      const drawn = range.getBoundingClientRect().width;

      // Guard against environments without layout, where every box is zero.
      if (!drawn || !base) return;
      node.style.fontSize = `${base * (available / drawn)}px`;
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = requestAnimationFrame(fit);
    };

    schedule();
    const observer = new ResizeObserver(schedule);
    observer.observe(frame);

    // Web fonts can land after first paint and change the metrics.
    document.fonts?.ready.then(schedule).catch(() => {});

    return () => {
      if (scheduled) cancelAnimationFrame(scheduled);
      observer.disconnect();
      range.detach();
    };
  }, []);

  useEffect(() => {
    const node = preRef.current;
    if (!node) return undefined;

    const { cols, rows, ramp, cells } = grid;
    const cellCount = cols * rows;
    const randomGlyph = () => ramp[1 + Math.floor(Math.random() * (ramp.length - 1))];

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

    // Per-cell moment (ms since start) at which the character stops churning.
    const settleAt = new Float32Array(cellCount);
    for (let i = 0; i < cellCount; i += 1) {
      settleAt[i] = Math.random() * SETTLE_MS;
    }

    // Per-cell timestamp of the last cursor disturbance.
    const disturbedAt = new Float32Array(cellCount).fill(-Infinity);

    let frame = 0;
    let startedAt = 0;
    let lastDraw = 0;
    let pointerCell = -1;

    const paint = (text) => {
      node.textContent = text;
    };

    const draw = (now) => {
      const elapsed = now - startedAt;
      let out = '';
      let busy = false;

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const i = row * cols + col;
          const settled = elapsed >= settleAt[i];
          if (!settled) busy = true;

          let scrambleChance = settled ? 0 : 1;

          if (pointerCell >= 0) {
            const dx = col - (pointerCell % cols);
            const dy = row - Math.floor(pointerCell / cols);
            const distance = Math.hypot(dx, dy);
            if (distance < RIPPLE_RADIUS) {
              disturbedAt[i] = now;
            }
          }

          const sinceDisturbed = now - disturbedAt[i];
          if (sinceDisturbed < RIPPLE_DECAY_MS) {
            busy = true;
            scrambleChance = Math.max(scrambleChance, 1 - sinceDisturbed / RIPPLE_DECAY_MS);
          }

          out += scrambleChance > 0 && Math.random() < scrambleChance ? randomGlyph() : cells[i];
        }
        out += '\n';
      }

      paint(out);
      return busy;
    };

    const loop = (now) => {
      if (!startedAt) startedAt = now;

      // Throttle: rAF fires at display refresh rate, which is more redraws than
      // the effect needs and more string building than it deserves.
      if (now - lastDraw >= 1000 / FPS) {
        lastDraw = now;
        if (!draw(now)) {
          paint(finalText);
          frame = 0;
          return;
        }
      }

      frame = requestAnimationFrame(loop);
    };

    const run = () => {
      if (frame) return;
      frame = requestAnimationFrame(loop);
    };

    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };

    if (motionQuery.matches) {
      paint(finalText);
    } else {
      run();
    }

    const onPointerMove = (event) => {
      if (motionQuery.matches || !finePointer.matches) return;
      const box = node.getBoundingClientRect();
      const col = Math.floor(((event.clientX - box.left) / box.width) * cols);
      const row = Math.floor(((event.clientY - box.top) / box.height) * rows);
      if (col < 0 || col >= cols || row < 0 || row >= rows) return;
      pointerCell = row * cols + col;
      startedAt = startedAt || performance.now();
      run();
    };

    const onPointerLeave = () => {
      pointerCell = -1;
    };

    // Nothing to animate while the tab is in the background.
    const onVisibility = () => {
      if (document.hidden) {
        stop();
        paint(finalText);
      }
    };

    node.addEventListener('pointermove', onPointerMove);
    node.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [grid, finalText]);

  return (
    <div ref={frameRef} className={[styles.frame, className].filter(Boolean).join(' ')}>
      <pre
        ref={preRef}
        className={styles.art}
        role={label ? 'img' : 'presentation'}
        aria-hidden={label ? undefined : true}
        aria-label={label}
        style={{ '--ascii-cols': grid.cols, '--ascii-rows': grid.rows }}
      >
        {/* Rendered resolved, then scrambled by the effect above. This way the
            artwork is present without JavaScript and there is no empty frame
            before the animation starts. The component never re-renders for a
            given grid, so mutating textContent afterwards is safe. */}
        {finalText}
      </pre>
    </div>
  );
}

export default AsciiArt;
